import React, { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

import { useAuth } from '../../hooks/useAuth';
import { openStripePortal } from '../../lib/billing';

export const PaymentMethodsPage: React.FC = () => {
  const { user, loading } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenPortal = useCallback(async () => {
    if (!user) {
      setError('You need to sign in to update your payment methods.');
      return;
    }

    setError(null);
    setPortalLoading(true);

    try {
      await openStripePortal(`${window.location.origin}/settings/payment-methods`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to open the billing portal.';
      const errorCode = (err as Error & { code?: string })?.code;

      // Provide helpful error messages based on error code
      let userFriendlyMessage = message;

      if (errorCode === 'NO_BILLING_HISTORY') {
        userFriendlyMessage = 'You don\'t have any payment methods on file yet. Subscribe to premium to add a payment method.';
      } else if (errorCode === 'NO_CUSTOMER_ID') {
        userFriendlyMessage = 'Unable to access your payment information. Please contact support if you have an active subscription.';
      } else if (errorCode === 'SUBSCRIPTION_NOT_FOUND') {
        userFriendlyMessage = 'No subscription found. Subscribe to premium to manage payment methods.';
      }

      setError(userFriendlyMessage);
      setPortalLoading(false);
    }
  }, [user]);

  // Redirect to home if not authenticated (after all hooks)
  if (!loading && !user) {
    return <Navigate to="/popular" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8">
        <div className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary/80">
            Settings · Payment Methods
          </p>
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            Manage Payment Methods
          </h1>
          <p className="text-base text-muted-foreground">
            Add, update, or remove the cards used for your Wallstreetle subscription.
          </p>
        </div>

        <Card className="bg-background/80">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-xl">Stripe Customer Portal</CardTitle>
              <CardDescription>
                All payment method changes happen directly in Stripe for your security.
              </CardDescription>
            </div>
            <div className="rounded-full bg-primary/10 p-3">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="rounded-md bg-muted/60 p-4 text-sm text-muted-foreground">
              <p>
                Stripe handles payment details so Wallstreetle never stores your card information. Updates you make in the portal take effect immediately.
              </p>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Opens a secure Stripe window with your stored payment methods.
            </p>
            <Button
              className="w-full sm:w-auto"
              onClick={handleOpenPortal}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening portal...
                </>
              ) : (
                <>
                  Manage in Stripe
                  <ExternalLink className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;
